<template>
  <v-dialog
    v-model="dialog"
    :max-width="760"
    @keydown.esc="close"
    style="z-index: 9998;"
  >
    <v-card>
      <v-toolbar dense>
        <v-icon>local_gas_station</v-icon>

        <template v-if="refueling === null">
          <v-tabs v-model="selectedTab" fixed-tabs class="px-4">
            <v-tab>{{ $t("dialog.refueling.tabs.data") }}</v-tab>
            <v-tab :disabled="result === null">{{ $t("dialog.refueling.tabs.summary") }}</v-tab>
          </v-tabs>
          <v-btn @click="actionStore(false)" elevation="0" color="primary" :disabled="selectedTab === 1">
            {{ $t("dialog.refueling.parse") }}
          </v-btn>
          <v-btn @click="actionStore(true)" elevation="0" color="error" class="ml-2" :disabled="storeDisabled">
            {{ $t("dialog.refueling.store") }}
          </v-btn>
        </template>
        <template v-else>
          <span class="pl-3">{{ $t("dialog.refueling.title_document") }}</span>
          <v-divider style="border: 0" />
          <v-btn v-if="refueling.document === null" @click="actionAdd()" elevation="0" color="primary">
            {{ $t("dialog.refueling.add") }}
          </v-btn>
          <v-btn v-else @click="actionUpdate()" elevation="0" color="primary">
            {{ $t("dialog.refueling.update") }}
          </v-btn>
        </template>

        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-tabs-items v-model="selectedTab">
        <v-tab-item class="overflow-y-auto">
          <v-container style="max-width: 720px; min-height: 60vh; max-height: 60vh">
            <v-expansion-panels flat hover v-model="selectedPanel">
              <v-expansion-panel v-bind:style="refueling !== null ? 'display: none' : ''">
                <v-expansion-panel-header>{{ $t("dialog.refueling.panels.file") }}</v-expansion-panel-header>
                <v-expansion-panel-content>

                  <validation-observer ref="file_validation">

                    <v-row>
                      <v-col>
                        <validation-provider
                          v-slot="{ errors }"
                          name="dialog.refueling.file"
                          rules="required"
                        >
                          <v-file-input
                            v-model="file"
                            clearable
                            :error-messages="errors"
                            :label="$t('dialog.refueling.file')" />
                        </validation-provider>
                      </v-col>
                    </v-row>
                  </validation-observer>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-header v-if="refueling === null">{{ $t("dialog.refueling.panels.handy") }}
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <validation-observer ref="form_validation">
                    <v-row>
                      <v-col class="my-0 pb-0">
                        <validation-provider
                          v-slot="{ errors }"
                          name="dialog.refueling.vehicle"
                          rules="required"
                        >
                          <VehicleField v-model="vehicle" :error-messages="errors" :disabled="refueling !== null" />
                        </validation-provider>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col class="my-0 py-0">
                        <validation-provider
                          v-slot="{ errors }"
                          name="dialog.refueling.date_time"
                          rules="required"
                        >
                          <DateTimeField
                            v-model="dateTime"
                            :error-messages="errors"
                            :disabled="refueling !== null"
                          />
                        </validation-provider>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="6" class="my-0 py-0">
                        <validation-provider
                          v-slot="{ errors }"
                          name="dialog.refueling.fuel"
                          rules="required"
                        >
                          <CodeField
                            v-model="fuel"
                            :error-messages="errors"
                            :label="$t('dialog.refueling.fuel')"
                            prefix="enum.fuel_type."
                            code-name="fuelTypes"
                            clearable
                          />
                        </validation-provider>
                      </v-col>

                      <v-col cols="4" class="my-0 py-0">
                        <validation-provider
                          v-slot="{ errors }"
                          name="dialog.refueling.quantity"
                          rules="required|decimal:2"
                        >
                          <v-text-field
                            v-model="quantity"
                            :error-messages="errors"
                            :label="$t('dialog.refueling.quantity')"
                            clearable
                          ></v-text-field>
                        </validation-provider>
                      </v-col>

                      <v-col cols="2" class="my-0 py-0">
                        <v-checkbox :label="$t('dialog.refueling.full')" v-model="full" />
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col class="my-0 py-0">
                        <validation-provider
                          v-slot="{ errors }"
                          name="dialog.refueling.amount"
                          rules="required"
                        >
                          <AmountField
                            v-model="amount"
                            resource-label="dialog.refueling.amount"
                            :error-messages="errors"
                          />
                        </validation-provider>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col class="my-0 py-0">
                        <validation-provider
                          v-slot="{ errors }"
                          name="dialog.refueling.place"
                          rules="max:120"
                        >
                          <v-text-field
                            v-model="place"
                            :counter="120"
                            :error-messages="errors"
                            :label="$t('dialog.refueling.place')"
                            clearable
                          ></v-text-field>
                        </validation-provider>
                      </v-col>
                    </v-row>

                  </validation-observer>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-container>
        </v-tab-item>
        <v-tab-item class="overflow-y-auto">
          <v-container style="max-width: 720px; min-height: 60vh; max-height: 60vh">
            <v-row v-if="result !== null">
              <v-data-table style="width: 700px; max-width: 700px; margin-left: 10px"
                            :items="result"
                            item-key="name"
                            class="elevation-0"
                            hide-default-header
                            hide-default-footer
                            fixed-header
                            :items-per-page="-1"
              >
                <template v-slot:header="">
                  <thead>
                  <tr>
                    <th>{{ $t("dialog.refueling.table.date_time") }}</th>
                    <th>{{ $t("dialog.refueling.table.numeric_plate") }}</th>
                    <th>{{ $t("dialog.refueling.table.fuel") }}</th>
                    <th style="text-align: end">{{ $t("dialog.refueling.table.quantity") }}</th>
                    <th style="text-align: end">{{ $t("dialog.refueling.table.amount") }}</th>
                    <th>{{ $t("dialog.refueling.table.place") }}</th>
                  </tr>
                  </thead>
                </template>
                <template v-slot:item="props">
                  <tr :class="{'table-row-hidden': props.item.result === 'skipped'}">
                    <td style="white-space: nowrap">{{ formatDate(props.item.dateTime) }}</td>
                    <td style="white-space: nowrap">{{ props.item.numericPlate }}</td>
                    <td style="white-space: nowrap; text-overflow: ellipsis; overflow: hidden; max-width: 200px">
                      {{ (props.item.fuel !== null) ? $t("enum.fuel_type." + props.item.fuel) : "" }}
                    </td>
                    <td style="white-space: nowrap; text-align: end">{{
                        formatQuantity(props.item.quantity)
                      }}
                    </td>
                    <td style="white-space: nowrap; text-align: end">{{
                        formatAmount(props.item.amount, props.item.currency)
                      }}
                    </td>
                    <td style="white-space: nowrap; text-overflow: ellipsis; overflow: hidden; max-width: 200px">{{
                        props.item.place
                      }}
                    </td>
                  </tr>
                  <tr v-if="props.item.result !== 'ok' && props.item.messages != null">
                    <td colspan="6">
                      <template v-for="error in props.item.messages">
                        <span v-bind:key="error"><span class="red--text">CHYBA:&nbsp;</span>{{ error }}<br /></span>
                      </template>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-row>
          </v-container>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <v-snackbar
      v-model="showResult"
      :timeout="2000"
      rounded="pill"
      bottom
      light
      color="black"
    >
      <span class="white--text">{{ resultMessage }}</span>
    </v-snackbar>

  </v-dialog>
</template>

<script>
//import utils from "@/utils";
import VehicleField from "@/components/fields/VehicleField.vue";
import CodeField from "@/components/fields/CodeField.vue";
import DateTimeField from "@/components/fields/DateTimeField.vue";
import AmountField from "@/components/fields/AmountField.vue";
import utils from "@/utils";
import { DateTime } from "luxon";
import { DateTimeValue } from "@/dateTimeValue";

export default {
  name: "RefuelingDialog",
  components: { DateTimeField, VehicleField, CodeField, AmountField },

  data() {
    return {
      dialog: false,
      refueling: null,

      selectedTab: 0,
      selectedPanel: 0,
      storeDisabled: false,

      showResult: false,
      resultMessage: null,

      file: null,

      vehicle: null,
      dateTime: null,
      fuel: null,
      full: null,
      quantity: null,
      amount: null,
      homeAmount: null,
      place: null,

      result: null
    };
  },

  methods: {
    open(refueling) {
      this.selectedTab = 0;

      this.clearForm();
      this.refueling = refueling;
      this.result = null;

      if (refueling !== null) {
        this.selectedPanel = 1;
        this.vehicle = refueling.vehicle;
        this.dateTime = DateTimeValue.fromString(refueling.timestamp);

        if (refueling.document !== null) {
          if (utils.isValue(refueling.document.fuel))
            this.fuel = refueling.document.fuel;
          if (utils.isValue(refueling.document.full))
            this.full = refueling.document.full;
          if (utils.isValue(refueling.document.quantity))
            this.quantity = utils.numberToString(refueling.document.quantity);
          if (utils.isValue(refueling.document.amount))
            this.amount = { amount: refueling.document.amount, currency: refueling.document.currency };
          if (utils.isValue(refueling.document.place))
            this.place = refueling.document.place;
        }
      } else
        this.selectedPanel = 0;

      this.dialog = true;
    },

    clearForm() {
      this.file = null;
      this.vehicle = null;
      this.dateTime = null;
      this.fuel = "diesel";
      this.full = null;
      this.quantity = null;
      this.amount = { amount: null, currency: "EUR" };
      this.homeAmount = null;
      this.place = null;

      this.resetValidators();
    },

    async actionAdd() {
      this.resetValidators();

      if (await this.$refs.form_validation.validate()) {
        const document = {
          fuel: this.fuel,
          full: this.full | false,
          quantity: utils.toNumber(this.quantity),
          place: this.place
        };

        if (this.amount !== null) {
          document.amount = this.amount.amount;
          document.currency = this.amount.currency;
        }

        if (this.homeAmount !== null) {
          document.homeAmount = this.homeAmount.amount;
          document.homecurrency = this.homeAmount.currency;
        }

        this.$store.dispatch("data/addRefuelingDocument", {
          vehicleId: this.vehicle.vehicleId,
          timestamp: this.refueling.timestamp,
          document: document
        }).then(() => {
          this.close();
        });
      }
    },

    async actionUpdate() {
      this.resetValidators();

      if (await this.$refs.form_validation.validate()) {
        const document = {
          fuel: this.fuel,
          full: this.full | false,
          quantity: utils.toNumber(this.quantity),
          place: this.place
        };

        if (this.amount !== null) {
          document.amount = this.amount.amount;
          document.currency = this.amount.currency;
        }

        if (this.homeAmount !== null) {
          document.homeAmount = this.homeAmount.amount;
          document.homecurrency = this.homeAmount.currency;
        }

        this.$store.dispatch("data/updateRefuelingDocument", {
          vehicleId: this.vehicle.vehicleId,
          timestamp: this.refueling.timestamp,
          document: document
        }).then(() => {
          this.close();
        });
      }
    },

    async actionStore(store) {
      this.resetValidators();

      if (this.selectedPanel === 0) {
        if (await this.$refs.file_validation.validate()) {
          this.$store.dispatch("data/importRefueling", {
            file: this.file,
            store: store
          }).then(result => {
            this.result = result.data;
            this.selectedTab = 1;
            this.storeDisabled = store;

            if (store)
              this.showResultMessage(result.data);
          });
        }
      } else {
        if (await this.$refs.form_validation.validate()) {
          var csv = "datetime;numeric_plate;fuel;quantity;full;amount;currency;place\n";
          csv = csv.concat(
            this.dateTime.date, " ", this.dateTime.time + ":00", ";",
            this.vehicle.numericPlate, ";",
            this.fuel, ";",
            this.quantity, ";",
            (this.full) ? "true" : "false", ";",
            (this.amount !== null) ? utils.numberToString(this.amount.amount) : "", ";",
            (this.amount !== null) ? this.amount.currency : "", ";",
            this.place || "", "\n"
          );

          const blob = new Blob([csv], {
            type: "text/csv"
          });

          this.$store.dispatch("data/importRefueling", {
            file: blob,
            store: store
          }).then(result => {
            this.result = result.data;
            this.selectedTab = 1;
            this.storeDisabled = store;

            if (store) {
              this.showResultMessage(result.data);

              this.clearForm();
            }
          });
        }
      }
    },

    showResultMessage(data) {
      this.resultMessage = this.$t("dialog.refueling.messages.result") + ": "
        + data.filter(x => x.result === "ok").length;
      this.showResult = true;
    },

    close() {
      this.resetValidators();
      this.dialog = false;
    },

    resetValidators() {
      if (this.$refs.form_validation !== undefined)
        this.$refs.form_validation.reset();
      if (this.$refs.file_validation !== undefined)
        this.$refs.file_validation.reset();
    },

    formatDate(date) {
      const d = DateTime.fromISO(date).toLocaleString(DateTime.DATE_SHORT) || "";
      const t = DateTime.fromISO(date).toLocaleString(DateTime.TIME_24_SIMPLE) || "";

      if (d !== "Invalid date" && t !== "Invalid date")
        return d + " " + t;
      else
        return "";
    },

    formatQuantity(value) {
      return utils.numberToString(value, 2, true) + " l";
    },

    formatAmount(amount, currency) {
      return utils.formatAmount(amount, currency) || "";
    }
  },

  watch: {
    dialog(newValue) {
      if (newValue === false)
        this.$store.dispatch("dialogs/hideDialog", "refueling");
    },

    selectedPanel() {
      this.result = null;
      this.resetValidators();
    },

    selectedTab() {
      if (this.selectedTab === 0)
        this.storeDisabled = false;
    }
  }
};
</script>

<style scoped>

</style>